// components/maintenance/MaintenanceDetailEditDialog.jsx
import React, { useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Grid, Box, FormControl, InputLabel, Select, MenuItem,
    FormHelperText, Typography
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

const ACT_TIP = ['INSPECCION', 'REEMPLAZO', 'AJUSTE', 'LIMPIEZA', 'LUBRICACION', 'REPARACION', 'OTRO'];
const DET_EST = ['PENDIENTE', 'EN_PROCESO', 'FINALIZADO', 'CANCELADO'];
const NEXT_ALLOWED = {
    PENDIENTE: ['PENDIENTE', 'EN_PROCESO', 'CANCELADO'],
    EN_PROCESO: ['EN_PROCESO', 'FINALIZADO', 'CANCELADO'],
    FINALIZADO: ['FINALIZADO'],
    CANCELADO: ['CANCELADO'],
};

const schema = yup.object({
    acttip: yup.string().nullable().oneOf([null, ...ACT_TIP]),
    faltades: yup.string().nullable().max(2000),
    acciondes: yup.string().nullable().max(2000),
    mandetest: yup.string().required().oneOf(DET_EST),
    mandetfecini: yup.date().nullable().typeError('Fecha inválida'),
    mandetfecfin: yup
        .date()
        .nullable()
        .typeError('Fecha inválida')
        .test('after-start', 'Fin no puede ser anterior al inicio', function (value) {
            const start = this.parent.mandetfecini;
            if (value && start) return value >= start;
            return true;
        }),
    mandetkm: yup.number().nullable().min(0, 'Debe ser >= 0').typeError('Debe ser numérico'),
    //mandettiehor: yup.number().nullable().min(0, 'Debe ser >= 0').typeError('Debe ser numérico'),
    mandetcostoparts: yup.number().nullable().min(0).typeError('Debe ser numérico'),
    mandetcostomano: yup.number().nullable().min(0).typeError('Debe ser numérico'),
    mandetcostootro: yup.number().nullable().min(0).typeError('Debe ser numérico'),
});

function toNumOrNull(v) {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
}
function isoOrNull(d) {
    if (!d) return null;
    const x = new Date(d);
    return Number.isNaN(x.getTime()) ? null : x.toISOString();
}

export default function MaintenanceDetailEditDialog({
    open,
    onClose,
    row,                 // detalle actual (lo que renderizas en la grilla)
    onSubmit,            // (payloadDiff) => Promise | void  -> tú llamas a updateMutation afuera
    isSaving = false,
}) {
    // Valores iniciales normalizados desde row
    const defaults = useMemo(() => ({
        mandetid: row?.mandetid ?? row?.manDetId ?? null,
        comid: row?.comid ?? null,
        componentenombre: row?.componentenombre ?? `Componente ${row?.comid}`,
        sistemanombre: row?.sistemanombre ?? '—',
        acttip: row?.acttip ?? null,
        faltades: row?.faltades ?? null,
        acciondes: row?.acciondes ?? null,
        mandetest: (row?.mandetest ?? row?.manDetEst ?? 'PENDIENTE').toUpperCase(),
        mandetfecini: row?.mandetfecini ? new Date(row.mandetfecini) : (row?.manDetFecIni ? new Date(row.manDetFecIni) : null),
        mandetfecfin: row?.mandetfecfin ? new Date(row.mandetfecfin) : (row?.manDetFecFin ? new Date(row.manDetFecFin) : null),
        mandetkm: row?.mandetkm ?? null,
        mandettiehor: row?.mandettiehor ?? null,
        mandetcostoparts: row?.mandetcostoparts != null ? Number(row.mandetcostoparts) : 0,
        mandetcostomano: row?.mandetcostomano != null ? Number(row.mandetcostomano) : 0,
        mandetcostootro: row?.mandetcostootro != null ? Number(row.mandetcostootro) : 0,
    }), [row]);

    const {
        control, handleSubmit, formState: { errors }, watch, reset,
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: defaults,
    });

    // Si cambia el row (por seguridad)
    React.useEffect(() => { reset(defaults); }, [defaults, reset]);

    // Calcula diferencias vs defaults (para no disparar 400 "sin cambios")
    const buildDiffPayload = (data) => {
        const diff = {};
        const map = [
            ['acttip', (v) => v ? String(v).toUpperCase() : null],
            ['faltades', (v) => v ?? null],
            ['acciondes', (v) => v ?? null],
            ['mandetest', (v) => String(v).toUpperCase()],
            ['mandetfecini', (v) => isoOrNull(v)],
            ['mandetfecfin', (v) => isoOrNull(v)],
            ['mandetkm', (v) => toNumOrNull(v)],
            ['mandettiehor', (v) => toNumOrNull(v)],
            ['mandetcostoparts', (v) => toNumOrNull(v) ?? 0],
            ['mandetcostomano', (v) => toNumOrNull(v) ?? 0],
            ['mandetcostootro', (v) => toNumOrNull(v) ?? 0],
        ];

        for (const [key, norm] of map) {
            const cur = norm(data[key]);
            const def = (() => {
                if (key === 'mandetfecini' || key === 'mandetfecfin') return isoOrNull(defaults[key]);
                if (key.startsWith('mandetcosto')) return defaults[key] ?? 0;
                return defaults[key];
            })();
            if (cur !== def) diff[key] = cur;
        }
        return diff;
    };

    const onSubmitForm = (data) => {
        const diff = buildDiffPayload(data);
        // El update del backend espera claves exactas como las de 'diff'
        // Si no hay cambios, evitamos la llamada
        if (Object.keys(diff).length === 0) {
            onSubmit({ _noChanges: true });
            return;
        }
        onSubmit(diff);
    };

    const current = watch('mandetest') || 'PENDIENTE';
    const options = NEXT_ALLOWED[current] ?? ['PENDIENTE'];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>Editar detalle #{defaults.mandetid}</DialogTitle>

            <DialogContent dividers>
                {/* Encabezado informativo */}
                <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Sistema: <strong>{defaults.sistemanombre}</strong>
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                        Componente: <strong>{defaults.componentenombre}</strong> (ID: {defaults.comid})
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                        Tiempo fuera de servicio: <strong>{defaults.mandettiehor}</strong> (ID: {defaults.comid})
                    </Typography>
                </Box>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid container spacing={2}>
                        {/* Estado del detalle */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                control={control}
                                name="mandetest"
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.mandetest}>
                                        <InputLabel>Estado</InputLabel>
                                        <Select label="Estado" {...field} value={field.value || 'PENDIENTE'}>
                                            {options.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                        </Select>
                                        {!!errors.mandetest && <FormHelperText>{errors.mandetest.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        {/* Tipo de actividad */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                control={control}
                                name="acttip"
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.acttip}>
                                        <InputLabel>Tipo de actividad</InputLabel>
                                        <Select label="Tipo de actividad" {...field} value={field.value ?? ''}>
                                            <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                            {ACT_TIP.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                        </Select>
                                        {!!errors.acttip && <FormHelperText>{errors.acttip.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        {/* Fechas */}
                        <Grid item xs={12} md={4}>
                            <Controller
                                control={control}
                                name="mandetfecini"
                                render={({ field: { onChange, value, ...rest } }) => (
                                    <DateTimePicker
                                        label="Fecha y hora de inicio"
                                        value={value}
                                        onChange={onChange}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                error={!!errors.mandetfecini}
                                                helperText={errors.mandetfecini?.message}
                                            />
                                        )}
                                        {...rest}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                control={control}
                                name="mandetfecfin"
                                render={({ field: { onChange, value, ...rest } }) => (
                                    <DateTimePicker
                                        label="Fecha y hora de fin"
                                        value={value}
                                        onChange={onChange}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                error={!!errors.mandetfecfin}
                                                helperText={errors.mandetfecfin?.message}
                                            />
                                        )}
                                        {...rest}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Controller
                                control={control}
                                name="mandetkm"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Km (opcional)"
                                        type="number"
                                        fullWidth
                                        error={!!errors.mandetkm}
                                        helperText={errors.mandetkm?.message}
                                        inputProps={{ min: 0 }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Diagnóstico / Acción */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                control={control}
                                name="faltades"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Diagnóstico / Falla (opcional)"
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        error={!!errors.faltades}
                                        helperText={errors.faltades?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller
                                control={control}
                                name="acciondes"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Acción realizada (opcional)"
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        error={!!errors.acciondes}
                                        helperText={errors.acciondes?.message}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Costos */}
                        <Grid item xs={12} md={4}>
                            <Controller
                                control={control}
                                name="mandetcostoparts"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Costo repuestos"
                                        type="number"
                                        fullWidth
                                        error={!!errors.mandetcostoparts}
                                        helperText={errors.mandetcostoparts?.message}
                                        inputProps={{ min: 0, step: '0.01' }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                control={control}
                                name="mandetcostomano"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Costo mano de obra"
                                        type="number"
                                        fullWidth
                                        error={!!errors.mandetcostomano}
                                        helperText={errors.mandetcostomano?.message}
                                        inputProps={{ min: 0, step: '0.01' }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                control={control}
                                name="mandetcostootro"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Otros costos"
                                        type="number"
                                        fullWidth
                                        error={!!errors.mandetcostootro}
                                        helperText={errors.mandetcostootro?.message}
                                        inputProps={{ min: 0, step: '0.01' }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </LocalizationProvider>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="inherit">Cancelar</Button>
                <Button
                    onClick={handleSubmit(onSubmitForm)}
                    variant="contained"
                    disabled={isSaving}
                >
                    Guardar cambios
                </Button>
            </DialogActions>
        </Dialog>
    );
}
